import React, { useEffect } from "react";
import {
  Page,
  Spinner,
  Card,
  Text,
  SkeletonPage,
  BlockStack,
  SkeletonDisplayText,
  SkeletonBodyText,
} from "@shopify/polaris";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  fetchFinanceDetailAsync,
  fetchFinanceDetailRefreshAsync,
  setDestroy,
} from "./finance-detail-container.slice";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import CustomerFinance from "components/customer-finance/customer-finance";
import { useTranslation } from "react-i18next";
import { Customer } from "types/Customer";

export default function FinanceDetailContainer() {
  const { t } = useTranslation();
  const { companyId, customerId, year, financeId } = useParams<{
    companyId: string;
    customerId: string;
    year: string;
    financeId: string;
  }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Pop back to the exact customer tab the user came from (preserves the
  // active tab + scroll, and the already-loaded customer so there's no
  // full-page reload flash). Fall back to the customer page only when this
  // view was opened directly with no in-app history to pop.
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(`/app/${companyId}/customers/${customerId}`);
    }
  };

  const {
    financeDetail,
    isLoading: isFinanceLoading,
    error: financeError,
  } = useAppSelector((state) => state.financeDetailReducer);

  const {
    customer,
    isLoading: isCustomerLoading,
    errMsg: customerError,
  } = useAppSelector((state) => state.customerReducer) as {
    customer: Customer | null;
    isLoading: boolean;
    errMsg: string | null;
  };

  const hasRouteCustomer = customer?.id === customerId;
  const isLoading = isFinanceLoading || isCustomerLoading;
  const error = financeError || (!hasRouteCustomer ? customerError : null);

  const handleFinanceUpdate = () => {
    if (companyId && customerId && year && financeId) {
      dispatch(
        fetchFinanceDetailRefreshAsync({
          companyId,
          customerId,
          year: parseInt(year, 10),
          financeId,
        })
      );
    }
  };

  useEffect(() => {
    if (!companyId || !customerId || !year) return;
    return () => {
      dispatch(setDestroy());
    };
  }, [companyId, customerId, year, dispatch]);

  useEffect(() => {
    if (!companyId || !customerId || !year || !financeId) return;
    // isEdit: the customer is usually already cached from the customer page —
    // refetch silently so its title/data stay visible while finance loads.
    dispatch(fetchCustomer({ companyId, customerId, isEdit: true }));
    dispatch(
      fetchFinanceDetailAsync({
        companyId,
        customerId,
        year: parseInt(year, 10),
        financeId,
      })
    );
  }, [companyId, customerId, year, financeId, dispatch]);

  // Skeleton whenever the page isn't ready to render (finance detail not loaded
  // yet, or the cached customer isn't the route's customer), mirroring the
  // centered single-column finance form instead of a bare spinner. This does
  // NOT depend on the loading flag: after a silent isEdit customer refetch the
  // customer can match immediately while financeDetail is still null and
  // isFinanceLoading hasn't flipped true yet — gating on isLoading there would
  // fall through to the null return and flash a blank page.
  if (!error && (!financeDetail || !hasRouteCustomer)) {
    return (
      <SkeletonPage backAction>
        <div className="flex justify-center w-full px-4 md:px-0">
          <div className="w-full lg:w-2/3 xl:w-1/2">
            <Card>
              <BlockStack gap="400">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={3} />
                <DataTableSkeleton rows={5} columns={4} />
                <SkeletonBodyText lines={2} />
              </BlockStack>
            </Card>
          </div>
        </div>
      </SkeletonPage>
    );
  }

  if (error) {
    return (
      <Page backAction={{ content: t("back"), onAction: handleBack }}>
        <Card>
          <Text as="p" tone="critical">
            {error}
          </Text>
        </Card>
      </Page>
    );
  }

  if (!financeDetail || !customer || !hasRouteCustomer) {
    return null;
  }

  return (
    <Page
      fullWidth
      backAction={{ content: t("back"), onAction: handleBack }}
      title={`${customer.name}${
        customer.legalName ? `, ${customer.legalName}` : ""
      } [${customer.externalCode}]`}
      subtitle={`${t("finance_approval")} - ${year}`}
      titleMetadata={isLoading && <Spinner size="small" />}
    >
      <div className="flex justify-center w-full px-4 md:px-0">
        <div className="w-full lg:w-2/3 xl:w-1/2">
          <CustomerFinance
            customer={customer}
            financeDetail={financeDetail}
            fileEntity={financeDetail?.masterAgreementFileId}
            onSubmit={handleFinanceUpdate}
          />
        </div>
      </div>
    </Page>
  );
}
